import React, { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/configureStore";
import OT from "@opentok/client";
import { VenueSessionState, validPublishingStates } from "../../../app/types";
import { useUser } from "../../api/helpers";
import {
  getSelectedAudioSource,
  getSelectedVideoSource,
} from "../../app/selectors";
import LeaveBreadcrumb from "../../logging/LeaveBreadcrumb";
import LogError from "../../logging/LogError";
import { PUBLISHER_VOLUME_FACTOR } from "../constants";
import { getSessionSetting } from "../selectors";
import { addMyStream, removeMyStream, updateVolume } from "../sessionSlice";
import useSessionId from "../useSessionId";

interface UsePublishCameraProps {
  vonageSession: OT.Session;
  sessionState: VenueSessionState;
  addStream: (streamToAdd: OT.Stream) => void;
  removeStream: (streamToRemove: OT.Stream) => void;
}

const usePublishCamera = ({
  sessionState,
  vonageSession,
  addStream,
  removeStream,
}: UsePublishCameraProps) => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const sessionId = useSessionId();
  const videoSource = useAppSelector(getSelectedVideoSource);
  const audioSource = useAppSelector(getSelectedAudioSource);
  const publishAudio =
    useAppSelector(getSessionSetting(sessionId, "publishAudio")) ?? true;
  const publishVideo =
    useAppSelector(getSessionSetting(sessionId, "publishVideo")) ?? true;

  const publisher = useRef<OT.Publisher>();

  const onAudioUpdated = useCallback(
    ({ audioLevel }: { audioLevel: number }) => {
      if (publisher.current?.stream?.streamId) {
        if (sessionId) {
          dispatch(
            updateVolume(
              sessionId,
              publisher.current?.stream?.streamId,
              audioLevel / PUBLISHER_VOLUME_FACTOR,
            ),
          );
        }
      }
    },
    [dispatch, sessionId],
  );

  const terminateMyStream = useCallback(() => {
    if (publisher.current?.stream) {
      removeStream(publisher.current.stream);
      if (sessionId) {
        dispatch(
          removeMyStream(
            sessionId,
            publisher.current.stream.streamId,
            "camera",
          ),
        );
      }
    }
    if (publisher.current) {
      publisher.current?.destroy();
      publisher.current = undefined;
    }
  }, [dispatch, removeStream, sessionId]);

  useEffect(() => {
    if (!validPublishingStates.includes(sessionState)) {
      terminateMyStream();
    }
    // Run only on session state changes
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [sessionState]);

  useEffect(
    () => () => terminateMyStream(),
    // Run on unmount
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [],
  );

  // Allow toggling of video && audio
  useEffect(() => {
    publisher.current?.publishVideo(publishVideo);
    publisher.current?.publishAudio(publishAudio);

    if (publishAudio) {
      publisher.current?.on("audioLevelUpdated", onAudioUpdated);
    } else {
      publisher.current?.off("audioLevelUpdated");
    }
  }, [dispatch, onAudioUpdated, publishAudio, publishVideo, sessionId]);

  // Allow changing of audio and video devices
  useEffect(() => {
    if (videoSource) {
      publisher.current?.setVideoSource(videoSource.deviceId).catch(LogError);
    }

    if (audioSource) {
      publisher.current?.setAudioSource(audioSource.deviceId).catch(LogError);
    }
  }, [publisher, audioSource, videoSource]);

  useEffect(() => {
    const publish = () => {
      if (publisher.current) {
        vonageSession.publish(publisher.current, (publishError) => {
          if (publishError) {
            if (publishError.name === "OT_NOT_CONNECTED") {
              setTimeout(publish, 1000);
            }
            LogError(publishError);
          } else {
            LeaveBreadcrumb("Published Camera");
          }
        });
      }
    };
    if (publisher.current === undefined && sessionState === "prePublish") {
      const options: OT.PublisherProperties = {
        insertDefaultUI: false,
        insertMode: "append",
        style: { buttonDisplayMode: "on" },
        width: "100%",
        height: "100%",
        fitMode: "contain",
        publishVideo,
        publishAudio,
        name: `${user?.firstName} ${user?.lastName}`,
        resolution: "1280x960",
        echoCancellation: true,
        showControls: true,
      };
      if (videoSource) {
        options.videoSource = videoSource.deviceId;
      }
      if (audioSource) {
        options.audioSource = audioSource.deviceId;
      }

      publisher.current = OT.initPublisher(
        undefined,
        options,
        (publishInitError) => {
          if (publishInitError) {
            LogError(publishInitError);
          } else {
            LeaveBreadcrumb("Initialized Camera");

            publisher.current?.on("streamDestroyed", () => {
              terminateMyStream();
            });

            publisher.current?.on("streamCreated", (event) => {
              if (sessionId) {
                dispatch(
                  addMyStream(
                    sessionId,
                    event.stream.streamId,
                    event.stream.videoType,
                  ),
                );
              }
              addStream(event.stream);
            });
            publisher.current?.on("audioLevelUpdated", onAudioUpdated);

            publish();
          }
        },
      );
    }

    return () => {
      publisher.current?.off("streamDestroyed");
      publisher.current?.off("streamCreated");
      publisher.current?.off("audioLevelUpdated");
    };
    // Don't re-run when audioSource, or videoSource changes
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    terminateMyStream,
    dispatch,
    sessionId,
    addStream,
    removeStream,
    publishAudio,
    publishVideo,
    sessionState,
    user?.firstName,
    user?.lastName,
    vonageSession,
    onAudioUpdated,
  ]);
};

export default usePublishCamera;
